import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { useAccess } from 'hooks/access/useAccess';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import { useContext } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';

interface ToolSelectionDefinition {
    iconSet: IconPrefix;
    icon: IconName;
    selected: boolean;
    onClick: () => void;
    label: string;
}

export const useMinuteTools = () => {
    const ctxEdit = useContext(CtxEdit);
    const { isExperimental } = useAccess();

    const largeButtons: ToolSelectionDefinition[] = [
        {
            iconSet: 'fal',
            icon: 'arrow-pointer',
            selected: ctxEdit.worldAction === EWorldAction.SELECT,
            onClick: () => {
                ctxEdit.setWorldAction(EWorldAction.SELECT);
            },
            label: Localization.MOUSE
        },
        {
            iconSet: 'fal',
            icon: 'location-dot',
            selected: ctxEdit.worldAction === EWorldAction.ICON_V2,
            onClick: () => {
                ctxEdit.setSelectedMarkers([]);
                ctxEdit.setWorldAction(EWorldAction.ICON_V2);
            },
            label: Localization.PIN
        },
        {
            iconSet: 'fal',
            icon: 'image-polaroid',
            selected: ctxEdit.worldAction === EWorldAction.IMAGE_V2,
            onClick: () => {
                ctxEdit.setWorldAction(EWorldAction.IMAGE_V2);
                ctxEdit.setSelectedMarkers([]);
            },
            label: Localization.IMAGE
        }
    ];

    const smallButtons: ToolSelectionDefinition[] = [
        {
            iconSet: 'fal',
            icon: 'route',
            selected: ctxEdit.worldAction === EWorldAction.DIRECTION,
            onClick: () => {
                ctxEdit.setWorldAction(EWorldAction.DIRECTION);
                ctxEdit.setSelectedMarkers([]);
            },
            label: Localization.DIRECTION
        },
        {
            iconSet: 'fal',
            icon: 'font',
            selected: ctxEdit.worldAction === EWorldAction.TEXT_V2,
            onClick: () => {
                ctxEdit.setWorldAction(EWorldAction.TEXT_V2);
                ctxEdit.setSelectedMarkers([]);
            },
            label: Localization.TEXT
        },
        {
            iconSet: 'fal',
            icon: 'pipe',
            selected: ctxEdit.worldAction === EWorldAction.LINE_V2,
            onClick: () => {
                ctxEdit.setSelectedMarkers([]);
                ctxEdit.setWorldAction(EWorldAction.LINE_V2);
            },
            label: Localization.LINE
        },
        {
            iconSet: 'fal',
            icon: 'draw-polygon',
            selected: ctxEdit.worldAction === EWorldAction.POLYGON_V2,
            onClick: () => {
                ctxEdit.setSelectedMarkers([]);
                ctxEdit.setWorldAction(EWorldAction.POLYGON_V2);
            },
            label: Localization.POLYGON
        },
        {
            iconSet: 'fal',
            icon: 'block-brick',
            selected: ctxEdit.worldAction === EWorldAction.LINE_V2_WALL,
            onClick: () => {
                ctxEdit.setSelectedMarkers([]);
                ctxEdit.setWorldAction(EWorldAction.LINE_V2_WALL);
            },
            label: Localization.WALL_LINE
        },
        {
            iconSet: 'fal',
            icon: 'person-shelter',
            selected: ctxEdit.worldAction === EWorldAction.INDOOR_STAND,
            onClick: () => {
                ctxEdit.setSelectedMarkers([]);
                ctxEdit.setWorldAction(EWorldAction.INDOOR_STAND);
            },
            label: Localization.STAND
        }
    ];

    const variantIconButton: ToolSelectionDefinition = {
        iconSet: 'fal',
        icon: 'map-marker',
        selected: ctxEdit.worldAction === EWorldAction.VARIANT_ICON_V2,
        onClick: () => {
            ctxEdit.setSelectedMarkers([]);
            ctxEdit.setWorldAction(EWorldAction.VARIANT_ICON_V2);
        },
        label: Localization.VARIANT_ICON
    };

    const uploadButton: ToolSelectionDefinition = {
        iconSet: 'fal',
        icon: 'upload',
        selected: ctxEdit.worldAction === EWorldAction.UPLOAD,
        onClick: () => {
            ctxEdit.setSelectedMarkers([]);
            ctxEdit.setWorldAction(EWorldAction.UPLOAD);
        },
        label: Localization.GPX_FILE
    };

    if (isExperimental) {
        smallButtons.push(variantIconButton, uploadButton);
    }

    return {
        largeButtons,
        smallButtons
    };
};
